/**
 * RBAC UI Helper Utilities
 * 
 * Convenience functions for integrating RBAC into UI components.
 * Provides simple APIs for checking permissions and rendering conditional UI.
 */

import { rbacService } from '../services/rbac-service.js';
import { Permission, ClipState, type User, type PermissionContext } from '../types/rbac.js';
import appState from '../core/app-state.js';
import logger from '../core/logger.js';

/**
 * Get the current authenticated user from app state
 */
export function getCurrentUser(): User | null {
  return appState.currentUser;
}

/**
 * Check if current user has a specific permission
 * 
 * @param permission - Permission to check
 * @returns true if user has permission
 */
export function can(permission: Permission): boolean {
  return rbacService.hasPermission(getCurrentUser(), permission);
}

/**
 * Check if current user has any of the specified permissions
 * 
 * @param permissions - Array of permissions to check
 * @returns true if user has at least one permission
 */
export function canAny(permissions: Permission[]): boolean {
  return rbacService.hasAnyPermission(getCurrentUser(), permissions);
}

/**
 * Check if current user has all of the specified permissions
 * 
 * @param permissions - Array of permissions to check
 * @returns true if user has all permissions
 */
export function canAll(permissions: Permission[]): boolean {
  return rbacService.hasAllPermissions(getCurrentUser(), permissions);
}

/**
 * Check if current user can approve content
 * 
 * @param permission - Approval permission (APPROVE_TEXT or APPROVE_AUDIO)
 * @param context - Context including who created the content
 * @returns true if user can approve
 */
export function canApprove(
  permission: Permission.APPROVE_TEXT | Permission.APPROVE_AUDIO,
  context: PermissionContext
): boolean {
  const result = rbacService.canApprove(getCurrentUser(), permission, context);
  return result.allowed;
}

/**
 * Get reason why user cannot approve (if applicable)
 * 
 * @param permission - Approval permission
 * @param context - Context including who created the content
 * @returns Reason string if denied, null if allowed
 */
export function getApprovalDenialReason(
  permission: Permission.APPROVE_TEXT | Permission.APPROVE_AUDIO,
  context: PermissionContext
): string | null {
  const result = rbacService.canApprove(getCurrentUser(), permission, context);
  return result.allowed ? null : (result.reason || 'Permission denied');
}

/**
 * Check if current user can transition a clip to a new state
 * 
 * @param fromState - Current clip state
 * @param toState - Target clip state
 * @param context - Additional context (e.g., createdBy)
 * @returns true if transition is allowed
 */
export function canTransitionState(
  fromState: ClipState,
  toState: ClipState,
  context?: PermissionContext
): boolean {
  const result = rbacService.canTransitionState(getCurrentUser(), fromState, toState, context);
  return result.allowed;
}

/**
 * Get reason why state transition is denied (if applicable)
 * 
 * @param fromState - Current clip state
 * @param toState - Target clip state
 * @param context - Additional context
 * @returns Reason string if denied, null if allowed
 */
export function getTransitionDenialReason(
  fromState: ClipState,
  toState: ClipState,
  context?: PermissionContext
): string | null {
  const result = rbacService.canTransitionState(getCurrentUser(), fromState, toState, context);
  return result.allowed ? null : (result.reason || 'Transition not allowed');
}

/**
 * Check if current user can publish content
 * 
 * @param context - Context including change type
 * @param minorFixAutoPublishEnabled - Optional toggle for MinorFix auto-publish
 * @returns true if user can publish
 */
export function canPublish(
  context: PermissionContext = {},
  minorFixAutoPublishEnabled: boolean = false
): boolean {
  const result = rbacService.canPublish(getCurrentUser(), context, minorFixAutoPublishEnabled);
  return result.allowed;
}

/**
 * Get reason why publish is denied (if applicable)
 * 
 * @param context - Context including change type
 * @param minorFixAutoPublishEnabled - Optional toggle
 * @returns Reason string if denied, null if allowed
 */
export function getPublishDenialReason(
  context: PermissionContext = {},
  minorFixAutoPublishEnabled: boolean = false
): string | null {
  const result = rbacService.canPublish(getCurrentUser(), context, minorFixAutoPublishEnabled);
  return result.allowed ? null : (result.reason || 'Cannot publish');
}

/**
 * Check if current user is an Admin
 */
export function isAdmin(): boolean {
  return rbacService.isAdmin(getCurrentUser());
}

/**
 * Check if current user is an Editor
 */
export function isEditor(): boolean {
  return rbacService.isEditor(getCurrentUser());
}

/**
 * Check if current user is a Reviewer
 */
export function isReviewer(): boolean {
  return rbacService.isReviewer(getCurrentUser());
}

/**
 * Get current user's role display name
 */
export function getCurrentUserRoleDisplayName(): string {
  const user = getCurrentUser();
  if (!user || !user.role) return 'User';
  return rbacService.getRoleDisplayName(user.role);
}

/**
 * Show or hide an element based on permission
 * 
 * @param element - DOM element to show/hide
 * @param permission - Permission required to show element
 */
export function showIfCan(element: HTMLElement | null, permission: Permission): void {
  if (!element) return;
  
  if (can(permission)) {
    element.style.display = '';
    element.removeAttribute('hidden');
  } else {
    element.style.display = 'none';
    element.setAttribute('hidden', '');
  }
}

/**
 * Enable or disable an element based on permission
 * 
 * @param element - DOM element to enable/disable
 * @param permission - Permission required to enable element
 * @param showTooltip - Optional tooltip message when disabled
 */
export function enableIfCan(
  element: HTMLElement | null, 
  permission: Permission,
  showTooltip: boolean = true
): void {
  if (!element) return;
  
  const hasPermission = can(permission);
  
  if (element instanceof HTMLButtonElement || element instanceof HTMLInputElement) {
    element.disabled = !hasPermission;
  } else {
    if (hasPermission) {
      element.classList.remove('disabled');
      element.removeAttribute('aria-disabled');
    } else {
      element.classList.add('disabled');
      element.setAttribute('aria-disabled', 'true');
    }
  }
  
  if (showTooltip && !hasPermission) {
    element.setAttribute('title', 'You do not have permission to perform this action');
  } else if (showTooltip) {
    element.removeAttribute('title');
  }
}

/**
 * Add a CSS class to an element based on permission
 * 
 * @param element - DOM element
 * @param permission - Permission to check
 * @param className - Class to add if permission is granted
 */
export function addClassIfCan(
  element: HTMLElement | null,
  permission: Permission,
  className: string
): void {
  if (!element) return;
  
  if (can(permission)) {
    element.classList.add(className);
  } else {
    element.classList.remove(className);
  }
}

/**
 * Log permission check for debugging
 * 
 * @param action - Action being attempted
 * @param permission - Permission being checked
 * @param context - Optional context
 */
export function logPermissionCheck(
  action: string,
  permission: Permission,
  context?: Record<string, unknown>
): void {
  const user = getCurrentUser();
  const hasPermission = can(permission);
  
  logger.debug('rbac-ui: Permission check', {
    action,
    permission,
    userId: user?.id,
    role: user?.role,
    granted: hasPermission,
    ...context
  });
}

/**
 * Create a permission guard function for event handlers
 * 
 * @param permission - Required permission
 * @param callback - Function to call if permission is granted
 * @param deniedMessage - Optional message to show if denied
 * @returns Guarded function
 */
export function guardWithPermission(
  permission: Permission,
  callback: () => void,
  deniedMessage?: string
): () => void {
  return () => {
    if (can(permission)) {
      callback();
    } else {
      logger.warn('rbac-ui: Action denied', { 
        permission, 
        userId: getCurrentUser()?.id 
      });
      
      if (deniedMessage) {
        // Could integrate with toast notification system here
        console.warn(deniedMessage);
      }
    }
  };
}

/**
 * Create a permission guard for approval actions
 * 
 * @param permission - Approval permission
 * @param context - Context including who created the content
 * @param callback - Function to call if approval is allowed
 * @param deniedCallback - Optional function to call if denied
 * @returns Guarded function
 */
export function guardApproval(
  permission: Permission.APPROVE_TEXT | Permission.APPROVE_AUDIO,
  context: PermissionContext,
  callback: () => void,
  deniedCallback?: (reason: string) => void
): () => void {
  return () => {
    const result = rbacService.canApprove(getCurrentUser(), permission, context);
    
    if (result.allowed) {
      callback();
    } else {
      logger.warn('rbac-ui: Approval denied', { 
        permission, 
        userId: getCurrentUser()?.id,
        reason: result.reason
      });
      
      if (deniedCallback && result.reason) {
        deniedCallback(result.reason);
      }
    }
  };
}

/**
 * Batch check multiple permissions and return results
 * 
 * @param permissions - Array of permissions to check
 * @returns Map of permission to boolean result
 */
export function checkPermissions(permissions: Permission[]): Map<Permission, boolean> {
  const user = getCurrentUser();
  const results = new Map<Permission, boolean>();
  
  permissions.forEach(permission => {
    results.set(permission, rbacService.hasPermission(user, permission));
  });
  
  return results;
}

/**
 * Get all permissions for current user
 * 
 * @returns Array of permissions
 */
export function getCurrentUserPermissions(): Permission[] {
  return rbacService.getUserPermissions(getCurrentUser());
}
